/*
 * <atc-total-price>
 * Shows the running order total on the Add to Cart button, e.g.
 * "Add to Cart – $79.92". It keeps the figure in sync when the shopper changes
 * the quantity, switches variant, or picks a bundle offer, and hides the total
 * while the button is disabled (sold out / unavailable combination).
 *
 * Two pricing sources, picked automatically per render:
 *  1. Kaching Bundles offer is present  -> total = (selected bar per-jar price)
 *     x (jars in that bundle). The per-jar price and the selection live only in
 *     Kaching's own UI (the single Shopify variant is always one fixed price),
 *     so we read them from Kaching's rendered bar. The jar count is captured
 *     ONLY at Kaching-authoritative moments (when Kaching selects/mounts an
 *     offer and sets the quantity input to the jar count). We never derive jars
 *     from the shopper's own quantity edits, and we keep the captured count
 *     through Kaching resetting the quantity to 1 after add-to-cart — which is
 *     what previously made the button fall back to the bare variant price.
 *  2. No Kaching offer (normal product) -> total = variant price x quantity,
 *     using the variant price from theme variant-change events.
 *
 * Markup it expects (rendered by snippets/buy-buttons.liquid):
 *   <button name="add" ...>
 *     <span>Add to Cart</span>
 *     <atc-total-price data-section-id data-unit-price data-money-format>
 *       <span class="product-form__total-dash"> – </span>
 *       <span data-atc-total>$XX.XX</span>
 *     </atc-total-price>
 *   </button>
 */
(function () {
  // Remembers how many jars each Kaching bundle offer represents, keyed by
  // sectionId + bar id. Captured only when Kaching itself sets the quantity to
  // the jar count; kept at module scope so it survives the custom element
  // disconnecting/reconnecting when Kaching re-renders the form on add-to-cart.
  var jarCountByBar = {};

  // Minimal port of Shopify's money formatter so we can turn a raw cents
  // amount into the shop's configured currency string (e.g. "$79.92").
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsPart = parts[1] ? decimal + parts[1] : '';
      return dollars + centsPart;
    }

    var match = formatString.match(placeholderRegex);
    var placeholder = match ? match[1] : 'amount';
    var value;
    switch (placeholder) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', '');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  // Parse a displayed price string ("$26.64", "1,299.97", "1.299,97") to cents.
  // Treats the last '.'/',' followed by 1-2 digits as the decimal separator and
  // any other separators as thousands grouping.
  function parseMoneyToCents(str) {
    if (!str) return NaN;
    var cleaned = str.replace(/[^0-9.,]/g, '');
    if (!cleaned) return NaN;
    var decimals = '';
    var intPart = cleaned;
    var m = cleaned.match(/[.,](\d{1,2})$/);
    if (m) {
      decimals = m[1];
      intPart = cleaned.slice(0, m.index);
    }
    intPart = intPart.replace(/[.,]/g, '');
    var dollars = parseInt(intPart || '0', 10);
    var cents = decimals ? parseInt((decimals + '00').slice(0, 2), 10) : 0;
    if (isNaN(dollars)) return NaN;
    return dollars * 100 + cents;
  }

  if (customElements.get('atc-total-price')) return;

  customElements.define(
    'atc-total-price',
    class AtcTotalPrice extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.unitPrice = parseInt(this.dataset.unitPrice, 10);
        if (isNaN(this.unitPrice)) this.unitPrice = 0;
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.output = this.querySelector('[data-atc-total]') || this;
        this.quantityInput = this.sectionId
          ? document.getElementById('Quantity-' + this.sectionId)
          : null;
        this.button = this.closest('button[name="add"]');
        // Visually-hidden polite live region (sibling of the button) so the
        // total change is announced to screen readers when it changes.
        var formScope = this.closest('product-form') || this.closest('form') || document;
        this.status = formScope.querySelector('[data-atc-total-status]');
        // Where to look for the Kaching offer bar — scoped to this section.
        this.kachingScope =
          (this.sectionId && document.getElementById('ProductInfo-' + this.sectionId)) || document;

        this.boundRender = this.render.bind(this);
        this.boundSyncAndRender = this.syncAndRender.bind(this);
        this._renderQueued = false;

        // The quantity custom element fires a bubbling "change" on the input for
        // both the +/- buttons and manual edits; "input" covers live typing.
        // These only re-render the display — they never capture the jar count,
        // so a shopper's manual quantity can't corrupt a bundle total.
        if (this.quantityInput) {
          this.quantityInput.addEventListener('change', this.boundRender);
          this.quantityInput.addEventListener('input', this.boundRender);
        }

        // Variant switches publish the freshly selected variant (price in cents).
        // Only relevant for normal products; harmless on Kaching products.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (!event || !event.data) return;
            if (
              this.sectionId &&
              event.data.sectionId != null &&
              String(event.data.sectionId) !== String(this.sectionId)
            ) {
              return;
            }
            var variant = event.data.variant;
            if (variant && typeof variant.price === 'number') {
              this.unitPrice = variant.price;
            }
            this.render();
          });
        }

        // Mirror the button's enabled state so the total disappears the moment
        // a selection becomes sold out or unavailable, and returns when valid.
        if (this.button) {
          this.disabledObserver = new MutationObserver(this.boundRender);
          this.disabledObserver.observe(this.button, {
            attributes: true,
            attributeFilter: ['disabled'],
          });
        }

        // Watch the section for Kaching mounting and for offer-selection changes
        // (Kaching toggles the "--selected" class and rewrites the form without
        // firing theme events). We react ONLY to mutations that touch Kaching's
        // own markup, so unrelated theme DOM churn — and our own writes — don't
        // trigger work or an infinite loop.
        this.kachingObserver = new MutationObserver((mutations) => {
          for (var i = 0; i < mutations.length; i++) {
            var mutation = mutations[i];
            var target = mutation.target;
            if (this.contains(target) || (this.status && this.status.contains(target))) continue;
            var el = target.nodeType === 1 ? target : target.parentElement;
            if (el && el.closest && el.closest('.kaching-bundles__bars, .kaching-bundles__bar')) {
              this.scheduleSyncAndRender();
              return;
            }
            // Catch Kaching mounting its bars into the section later.
            for (var j = 0; j < mutation.addedNodes.length; j++) {
              var node = mutation.addedNodes[j];
              if (
                node.nodeType === 1 &&
                ((node.matches && node.matches('.kaching-bundles__bars')) ||
                  (node.querySelector && node.querySelector('.kaching-bundles__bars')))
              ) {
                this.scheduleSyncAndRender();
                return;
              }
            }
          }
        });
        this.kachingObserver.observe(this.kachingScope, {
          attributes: true,
          attributeFilter: ['class'],
          childList: true,
          subtree: true,
        });

        // Initial render, plus deferred re-syncs: Kaching may push the jar
        // quantity into the input a tick or two after mounting/selection (via a
        // silent .value write that fires no event), and the element reconnects
        // after add-to-cart — these catch up to the settled state.
        this.syncAndRender();
        this._rafId = window.requestAnimationFrame(this.boundSyncAndRender);
        this._deferTimer = window.setTimeout(this.boundSyncAndRender, 250);
      }

      disconnectedCallback() {
        if (this.quantityInput) {
          this.quantityInput.removeEventListener('change', this.boundRender);
          this.quantityInput.removeEventListener('input', this.boundRender);
        }
        if (this.variantUnsubscriber) this.variantUnsubscriber();
        if (this.disabledObserver) this.disabledObserver.disconnect();
        if (this.kachingObserver) this.kachingObserver.disconnect();
        if (this._rafId) window.cancelAnimationFrame(this._rafId);
        if (this._deferTimer) window.clearTimeout(this._deferTimer);
      }

      scheduleSyncAndRender() {
        if (this._renderQueued) return;
        this._renderQueued = true;
        window.requestAnimationFrame(() => {
          this._renderQueued = false;
          this.syncAndRender();
        });
      }

      getQuantity() {
        if (!this.quantityInput) return 1;
        var qty = parseInt(this.quantityInput.value, 10);
        if (isNaN(qty) || qty < 1) return 1;
        return qty;
      }

      getSelectedBar() {
        return this.kachingScope.querySelector('.kaching-bundles__bar--selected');
      }

      cacheKey(barId) {
        return (this.sectionId || 'nosec') + '|' + (barId || 'default');
      }

      // Capture the jar count from the quantity input at a Kaching-authoritative
      // moment (selection/mount). "Only raise" so Kaching resetting the quantity
      // to 1 after add-to-cart never erases the real bundle count; a single-jar
      // offer still records 1 the first time it's seen.
      syncJarCount() {
        var bar = this.getSelectedBar();
        if (!bar) return;
        var key = this.cacheKey(bar.getAttribute('data-deal-bar-id'));
        var qty = this.getQuantity();
        if (qty > 1) jarCountByBar[key] = qty;
        else if (jarCountByBar[key] == null) jarCountByBar[key] = qty || 1;
      }

      syncAndRender() {
        this.syncJarCount();
        this.render();
      }

      // Total from the currently selected Kaching bundle offer, in cents, or
      // null when no Kaching offer is on the page (fall back to variant pricing).
      // Read-only: never writes the jar-count cache.
      getKachingTotal() {
        var bar = this.getSelectedBar();
        if (!bar) return null;
        var priceEl = bar.querySelector('.kaching-bundles__bar-price');
        if (!priceEl) return null;
        // Strip the "/ per jar" unit label so we parse only the number.
        var clone = priceEl.cloneNode(true);
        var unitLabel = clone.querySelector('.kaching-bundles__bar-price__unit-label');
        if (unitLabel) unitLabel.remove();
        var perJar = parseMoneyToCents(clone.textContent);
        if (!perJar || isNaN(perJar)) return null;

        var jars = jarCountByBar[this.cacheKey(bar.getAttribute('data-deal-bar-id'))];
        if (!jars) {
          // Not captured yet — best effort from the live quantity.
          var qty = this.getQuantity();
          jars = qty > 0 ? qty : 1;
        }
        return perJar * jars;
      }

      render() {
        var disabled = this.button ? this.button.hasAttribute('disabled') : false;
        if (disabled) {
          this.hidden = true;
          if (this.status) this.status.textContent = '';
          return;
        }
        this.hidden = false;

        var total = this.getKachingTotal();
        if (total == null) total = this.unitPrice * this.getQuantity();

        var formatted = formatMoney(total, this.moneyFormat);
        this.output.innerHTML = formatted;
        // Announce the plain-text total (strip any markup the money format carries).
        if (this.status) this.status.textContent = formatted.replace(/<[^>]*>/g, '');
      }
    }
  );
})();
